import cx from 'classnames'
import { useState } from 'react'
import { Pressable } from '@react-aria/interactions'

import Styles from './ManageVirtualMarketerPlanModal.module.scss'
import VirtualMarketerPlansModalStyles from '../VirtualMarketerPlansModal/VirtualMarketerPlansModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { StripePrice, VirtualMarketerPlan } from '../types'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CreditCardFormModal } from '../VirtualMarketerPlansModal/components/CreditCardForm/CreditCardFormModal'
import { useHistory, useLocation } from 'react-router-dom'
import Review from 'blaze-ui/icons/32/Review'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const showBlackFridayOfferLabel = (price: StripePrice, isBlackFridayFlagOn: boolean) => {
  return price.recurring?.interval_count === 12 && isBlackFridayFlagOn
}

export const ManageVirtualMarketerPlanModal = ({
  plan,
  currentPlanIntervalCount,
}: {
  plan: VirtualMarketerPlan
  currentPlanIntervalCount: number
}) => {
  const isBlackFriday = useFlipperFlag('black_friday_cta')
  const { openModal, closeModal, closeAllModals } = useModals()
  const workspace = useCurrentWorkspace()
  const [selectedPrice, setSelectedPrice] = useState<StripePrice | undefined>(
    plan.prices.find(
      (price) => price.recurring.interval_count === currentPlanIntervalCount && !price.metadata.additional_key,
    ),
  )
  const location = useLocation()
  const history = useHistory()

  const handleCancel = () => {
    openModal(UpgradePlanModal, { initialPage: 'provideReason', onClose: closeModal, showSuccessModal: false })
  }

  const handleChangePlan = async () => {
    if (!workspace || !selectedPrice) {
      showToastMessage(
        'Unable to upgrade your subscription. Please refresh your browser and try again, contact support if issue persists.',
        'error',
      )
      return
    }

    const successMessage = `You've upgraded to the ${
      selectedPrice.recurring.interval_count === 1
        ? 'Month to month'
        : `${selectedPrice.recurring.interval_count} month`
    } plan!`

    openModal(CreditCardFormModal, {
      chosenPrice: selectedPrice,
      onBack: closeModal,
      onSuccess: () => {
        showToastMessage(successMessage, 'success')
        closeAllModals()
        const queryParams = new URLSearchParams(location.search)
        queryParams.delete('settings')
        setTimeout(() => {
          history.push({ search: queryParams.toString() })
        }, 250)
      },
      handleOutsidePress: closeModal,
    })
    return
  }

  const submitButtonText = workspace?.paying ? 'Manage Subscription' : 'Subscribe'

  return (
    <Modal.Root bodyClassName={Styles.modalBody} onPressOutside={closeModal}>
      <Modal.Header>
        <div className={Styles.header}>
          <Modal.BackButton onPress={closeModal} />
          <Heading level={3}>{workspace?.paying ? 'Manage Subscription' : 'Choose Plan'}</Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.container}>
          {plan.prices
            .filter((price) => !price?.metadata?.additional_key)
            .map((price) => {
              return (
                <Pressable onPress={() => setSelectedPrice(price)} key={price.id}>
                  <div
                    className={cx(VirtualMarketerPlansModalStyles.price, {
                      [VirtualMarketerPlansModalStyles.selected]: selectedPrice?.id === price.id,
                      [VirtualMarketerPlansModalStyles.isBlackFridayOffer]: showBlackFridayOfferLabel(
                        price,
                        isBlackFriday,
                      ),
                    })}
                  >
                    <div
                      className={cx(VirtualMarketerPlansModalStyles.transparentBorder, {
                        [VirtualMarketerPlansModalStyles.selected]: selectedPrice?.id === price.id,
                      })}
                    >
                      <div className={VirtualMarketerPlansModalStyles.planLeft}>
                        <Heading level={3}>
                          {price.recurring?.interval_count === 1
                            ? 'Monthly'
                            : `${price.recurring?.interval_count} month`}{' '}
                          plan
                        </Heading>
                      </div>
                      <div className={VirtualMarketerPlansModalStyles.planRight}>
                        <div className={VirtualMarketerPlansModalStyles.top}>
                          <Heading level={2}>
                            ${Math.ceil(price.unit_amount / 100 / price.recurring?.interval_count)}/mo
                          </Heading>
                          {(price.metadata.percent_off?.length || 0) > 0 && (
                            <div
                              className={cx(VirtualMarketerPlansModalStyles.percentOff, {
                                [VirtualMarketerPlansModalStyles.isBlackFridayOffer]: showBlackFridayOfferLabel(
                                  price,
                                  isBlackFriday,
                                ),
                              })}
                            >
                              {price.metadata.percent_off}% off
                            </div>
                          )}
                        </div>
                        {price.recurring?.interval_count > 1 && (
                          <Text variant="secondary">${price.unit_amount / 100} total billed</Text>
                        )}
                      </div>
                      {workspace?.paying && currentPlanIntervalCount === price.recurring.interval_count && (
                        <div className={Styles.currentPlan}>Current Plan</div>
                      )}
                      {showBlackFridayOfferLabel(price, isBlackFriday) && (
                        <div className={Styles.blackFridaySale}>Black Friday Sale</div>
                      )}
                      {!workspace?.paying &&
                        selectedPrice?.id === price.id &&
                        price.recurring?.interval_count === 6 && (
                          <span className={VirtualMarketerPlansModalStyles.mostPopular}>Most Popular</span>
                        )}
                    </div>
                  </div>
                </Pressable>
              )
            })}
          {selectedPrice && (
            <div className={Styles.footer}>
              <Review />
              <Text variant="largeList" color="var(--dark-80)">
                Add users for just $15/month in Settings
              </Text>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button onPress={closeModal} variant="tertiary" size="lg">
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.footerRightButtons}>
            {workspace?.paying && (
              <Button variant="secondary" size="lg" onPress={handleCancel}>
                Cancel Subscription
              </Button>
            )}
            <Button size="lg" onPress={handleChangePlan}>
              {submitButtonText}
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
